import React, { useState, useEffect } from 'react'
import { cn } from '../../../lib/utils'
import { Progress } from '../../../components/ui/Progress'
import { Badge } from '../../../components/ui/Badge'
import { Card, CardContent, CardHeader } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  TrendingUp, 
  Target,
  Users,
  BarChart3,
  Shield,
  Database,
  Settings,
  Brain,
  Award,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
  Zap
} from 'lucide-react'

interface ProgressStepData {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  businessValue: string
  estimatedTime: string
  status: 'completed' | 'current' | 'upcoming' | 'skipped'
  completionQuality?: 'excellent' | 'good' | 'basic' | 'incomplete'
  valuePoints?: number
}

interface EnterpriseProgressBarProps {
  currentScreen: string
  totalProgress: number
  hasValidationErrors?: boolean
  className?: string
}

const screenConfigs: Record<string, ProgressStepData> = {
  'industry-selection': {
    id: 'industry',
    title: 'Industry Selection',
    description: 'Define your market focus and industry context',
    icon: <Target className="h-5 w-5" />,
    businessValue: 'Market-specific intelligence targeting',
    estimatedTime: '2 min',
    status: 'completed',
    completionQuality: 'excellent',
    valuePoints: 15
  },
  'business-profile': {
    id: 'profile',
    title: 'Business Profile',
    description: 'Configure your organizational parameters',
    icon: <Users className="h-5 w-5" />,
    businessValue: 'Personalized intelligence recommendations',
    estimatedTime: '3 min',
    status: 'completed',
    completionQuality: 'good',
    valuePoints: 20
  },
  'revenue-intelligence': {
    id: 'revenue',
    title: 'Revenue Intelligence',
    description: 'Set up revenue tracking and market analysis',
    icon: <TrendingUp className="h-5 w-5" />,
    businessValue: 'Revenue optimization insights',
    estimatedTime: '4 min',
    status: 'current',
    valuePoints: 25
  },
  'supply-chain-intelligence': {
    id: 'supply',
    title: 'Supply Chain Intelligence',
    description: 'Configure supply chain monitoring and risk assessment',
    icon: <BarChart3 className="h-5 w-5" />,
    businessValue: 'Supply chain risk mitigation',
    estimatedTime: '3 min',
    status: 'upcoming',
    valuePoints: 20
  },
  'competitive-intelligence': {
    id: 'competitive',
    title: 'Competitive Intelligence',
    description: 'Enable competitor monitoring and market positioning',
    icon: <Shield className="h-5 w-5" />,
    businessValue: 'Competitive advantage insights',
    estimatedTime: '4 min',
    status: 'upcoming',
    valuePoints: 25
  },
  'data-integration': {
    id: 'integration',
    title: 'Data Integration',
    description: 'Connect your data sources and systems',
    icon: <Database className="h-5 w-5" />,
    businessValue: 'Unified intelligence dashboard',
    estimatedTime: '5 min',
    status: 'upcoming',
    valuePoints: 30
  },
  'intelligence-delivery': {
    id: 'delivery',
    title: 'Intelligence Delivery',
    description: 'Configure how you receive intelligence insights',
    icon: <Settings className="h-5 w-5" />,
    businessValue: 'Automated intelligence delivery',
    estimatedTime: '2 min',
    status: 'upcoming',
    valuePoints: 15
  },
  'intelligence-summary': {
    id: 'summary',
    title: 'Intelligence Summary',
    description: 'Review and finalize your intelligence configuration',
    icon: <Award className="h-5 w-5" />,
    businessValue: 'Complete strategic intelligence platform',
    estimatedTime: '3 min',
    status: 'upcoming',
    valuePoints: 35
  }
}

const getQualityColor = (quality?: string) => {
  switch (quality) {
    case 'excellent': return 'text-green-600 bg-green-50 border-green-200'
    case 'good': return 'text-blue-600 bg-blue-50 border-blue-200'
    case 'basic': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    case 'incomplete': return 'text-red-600 bg-red-50 border-red-200'
    default: return 'text-gray-600 bg-gray-50 border-gray-200'
  }
}

const getStatusIcon = (status: string, quality?: string) => {
  switch (status) {
    case 'completed':
      if (quality === 'excellent') return <CheckCircle2 className="h-4 w-4 text-green-600" />
      if (quality === 'good') return <CheckCircle2 className="h-4 w-4 text-blue-600" />
      if (quality === 'basic') return <CheckCircle2 className="h-4 w-4 text-yellow-600" />
      return <CheckCircle2 className="h-4 w-4 text-green-600" />
    case 'current':
      return <Clock className="h-4 w-4 text-blue-600 animate-pulse" />
    case 'upcoming':
      return <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
    default:
      return <AlertCircle className="h-4 w-4 text-gray-400" />
  }
}

// Smart default collapsed state based on context
const getSmartDefault = (hasValidationErrors: boolean, isMobile: boolean, isFirstVisit: boolean) => {
  if (hasValidationErrors) return true // Expand on validation errors
  if (isMobile) return false // Default collapsed on mobile
  if (isFirstVisit) return true // Expand for first-time users
  return false // Default collapsed for return users
}

export const EnterpriseProgressBar: React.FC<EnterpriseProgressBarProps> = ({
  currentScreen,
  totalProgress,
  hasValidationErrors = false,
  className
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [isFirstVisit, setIsFirstVisit] = useState(true)
  const [isExpanded, setIsExpanded] = useState(false)
  const [userHasToggled, setUserHasToggled] = useState(false)

  // Detect mobile and first visit
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    const checkFirstVisit = () => {
      const hasVisited = localStorage.getItem('onboarding-progress-visited')
      setIsFirstVisit(!hasVisited)
      if (!hasVisited) {
        localStorage.setItem('onboarding-progress-visited', 'true')
      }
    }

    checkMobile()
    checkFirstVisit()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Smart default state
  useEffect(() => {
    if (!userHasToggled) {
      const shouldExpand = getSmartDefault(hasValidationErrors, isMobile, isFirstVisit)
      setIsExpanded(shouldExpand)
    }
  }, [hasValidationErrors, isMobile, isFirstVisit, userHasToggled])

  // Remember user preference
  useEffect(() => {
    const savedState = localStorage.getItem('progress-bar-expanded')
    if (savedState !== null && userHasToggled) {
      setIsExpanded(savedState === 'true')
    }
  }, [userHasToggled])

  const handleToggle = () => {
    const newState = !isExpanded
    setIsExpanded(newState)
    setUserHasToggled(true)
    localStorage.setItem('progress-bar-expanded', newState.toString())
  }

  const currentStep = screenConfigs[currentScreen]
  const allSteps = Object.values(screenConfigs)
  
  // Calculate accumulated business value
  const completedSteps = allSteps.filter(step => step.status === 'completed')
  const totalValueAccumulated = completedSteps.reduce((sum, step) => sum + (step.valuePoints || 0), 0)
  const maxPossibleValue = allSteps.reduce((sum, step) => sum + (step.valuePoints || 0), 0)
  
  // Calculate estimated time remaining
  const upcomingSteps = allSteps.filter(step => step.status === 'upcoming' || step.status === 'current')
  const estimatedTimeRemaining = upcomingSteps.reduce((total, step) => {
    const timeInMinutes = parseInt(step.estimatedTime.replace(' min', ''))
    return total + timeInMinutes
  }, 0)

  // Set status for current step
  const stepsWithCurrentStatus = allSteps.map(step => {
    if (step.id === currentStep?.id) {
      return { ...step, status: 'current' as const }
    }
    return step
  })

  // Get current step number
  const currentStepNumber = Object.keys(screenConfigs).findIndex(key => key === currentScreen) + 1
  const totalSteps = Object.keys(screenConfigs).length

  // Get validation status for brief view
  const getValidationStatus = () => {
    if (hasValidationErrors) {
      return { icon: <AlertCircle className="h-4 w-4 text-red-500" />, text: 'Needs attention', color: 'text-red-600' }
    }
    return { icon: <CheckCircle2 className="h-4 w-4 text-green-500" />, text: 'Ready to proceed', color: 'text-green-600' }
  }

  const validationStatus = getValidationStatus()

  return (
    <Card className={cn("mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100 transition-all duration-300", className)}>
      {/* Brief View Header - Always Visible */}
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Brain className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Setup Progress
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Step {currentStepNumber} of {totalSteps}: {currentStep?.title}</span>
                <span>•</span>
                <div className="flex items-center gap-1">
                  {validationStatus.icon}
                  <span className={validationStatus.color}>{validationStatus.text}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Brief Progress Info */}
            <div className="text-right">
              <Badge 
                variant="secondary" 
                className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 border-blue-200"
              >
                {totalProgress}% Complete
              </Badge>
              <div className="text-xs text-gray-500 mt-1">
                ~{estimatedTimeRemaining} min remaining
              </div>
            </div>
            
            {/* Toggle Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggle}
              className="p-2 hover:bg-blue-100 transition-colors"
              title={isExpanded ? "Collapse detailed view" : "Expand detailed view"}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-blue-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-blue-600" />
              )}
            </Button>
          </div>
        </div>

        {/* Brief Progress Bar - Always Visible */}
        <div className="mt-3 space-y-2">
          <Progress value={totalProgress} className="h-2 bg-gray-200" />
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>Configuration Progress</span>
            <span>Intelligence Value: {totalValueAccumulated}/{maxPossibleValue} points</span>
          </div>
        </div>
      </CardHeader>

      {/* Detailed View - Collapsible Content */}
      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <CardHeader className="pt-0 pb-4">
          {/* Current Step Highlight */}
          {currentStep && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                  {currentStep.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">
                      {currentStep.title}
                    </h4>
                    <Badge variant="outline" className="text-xs">
                      Active Step
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {currentStep.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1 text-blue-600">
                      <TrendingUp className="h-3 w-3" />
                      <span>{currentStep.businessValue}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>{currentStep.estimatedTime}</span>
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <Target className="h-3 w-3" />
                      <span>+{currentStep.valuePoints} points</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-0">
          {/* Step-by-step Progress Overview */}
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-medium text-gray-700">Progress Overview</h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggle}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                <Minimize2 className="h-3 w-3 mr-1" />
                Minimize
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {stepsWithCurrentStatus.map((step, index) => (
                <div
                  key={step.id}
                  className={cn(
                    "p-3 rounded-lg border transition-all duration-200",
                    step.status === 'completed' 
                      ? "bg-green-50 border-green-200" 
                      : step.status === 'current'
                      ? "bg-blue-50 border-blue-200 ring-2 ring-blue-100"
                      : "bg-gray-50 border-gray-200"
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(step.status, step.completionQuality)}
                    <span className="text-xs font-medium text-gray-900 truncate">
                      {step.title}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {step.estimatedTime}
                    </span>
                    {step.status === 'completed' && step.completionQuality && (
                      <Badge 
                        className={cn(
                          "text-xs px-2 py-0.5 border",
                          getQualityColor(step.completionQuality)
                        )}
                      >
                        {step.completionQuality}
                      </Badge>
                    )}
                    {step.status === 'current' && (
                      <Badge variant="outline" className="text-xs px-2 py-0.5 border-blue-200 text-blue-700">
                        In Progress
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Value Accumulation Display */}
          <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Award className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h6 className="font-semibold text-green-900">
                    Intelligence Value Accumulated
                  </h6>
                  <p className="text-sm text-green-700">
                    Unlocking strategic insights for your business
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-700">
                  {totalValueAccumulated}
                </div>
                <div className="text-sm text-green-600">
                  of {maxPossibleValue} points
                </div>
              </div>
            </div>
            
            {/* Value Progress Bar */}
            <div className="mt-3">
              <Progress 
                value={(totalValueAccumulated / maxPossibleValue) * 100} 
                className="h-2 bg-green-100" 
              />
            </div>
          </div>

          {/* Smart Insights - Contextual */}
          {(hasValidationErrors || totalProgress > 50) && (
            <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-indigo-600 mt-0.5" />
                <div>
                  <h6 className="font-semibold text-indigo-900 mb-2">Smart Insights</h6>
                  <ul className="space-y-1 text-sm text-indigo-800">
                    {hasValidationErrors && (
                      <li>• Complete the highlighted fields to unlock the next intelligence module.</li>
                    )}
                    {totalProgress >= 75 && (
                      <li>• Excellent progress! You're building a comprehensive intelligence platform.</li>
                    )}
                    {totalProgress >= 50 && totalProgress < 75 && (
                      <li>• You're halfway there! Each step adds significant business value.</li>
                    )}
                    <li>• Your industry-specific configurations will provide targeted strategic intelligence.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  )
}